import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../layout/Sidebar";
import Topbar from "../layout/Topbar";
import { getJobs } from "../services/jobService";
import "../styles/JobReports.css";
import API from "../services/api";

/* ─────────────────── Constants ─────────────────── */
const ROWS_OPTIONS = [5, 10, 25];
const DATE_RANGES  = ["Today", "This Week", "This Month", "This Year", "Custom"];

const FILTER_TYPE_MAP = {
  "Today":      "today",
  "This Week":  "week",
  "This Month": "month",
  "This Year":  "year",
  "Custom":     "custom",
};

const STATUS_COLORS = {
  pending:   "badge-amber",
  ongoing:   "badge-blue",
  hold:      "badge-purple",
  completed: "badge-green",
  revisit:   "badge-teal",
};

const ACTIVITY_COLORS = {
  "Material Collection": "badge-teal",
  "Travelling":          "badge-blue",
  "Wrap Preparation":    "badge-purple",
  "Hold":                "badge-amber",
  "Installation":        "badge-green",
};

const MATERIAL_COLORS = {
  "Paint":            "badge-amber",
  "Paint Brushes":    "badge-amber",
  "Drywall Sheets":   "badge-blue",
  "Joint Compound":   "badge-teal",
  "Vinyl Wrap Rolls": "badge-purple",
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const fmtDuration = (mins) => {
  if (mins === null || mins === undefined) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const fmtHM = (mins) => {
  const h = Math.floor(mins / 60), m = mins % 60;
  return `${h}h ${m}m`;
};

const fmtJobId = (id) => `JOB-${String(id).padStart(4, "0")}`;

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const apiGet = (path, params) => API.get(path, { params });

/* ─── Export helpers ─── */
function downloadCSV(filename, headers, rows) {
  const blob = new Blob([[headers, ...rows].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

function downloadPDF(title, headers, rows) {
  const win = window.open("", "_blank");
  win.document.write(`<html><head><title>${title}</title>
    <style>body{font-family:Arial,sans-serif;font-size:12px;padding:20px}h2{margin-bottom:16px;color:#111}
    table{width:100%;border-collapse:collapse}th{background:#2563eb;color:#fff;padding:8px 10px;text-align:left;font-size:11px;letter-spacing:.05em}
    td{padding:7px 10px;border-bottom:1px solid #e5e7eb;color:#374151}tr:nth-child(even) td{background:#f9fafb}</style></head>
    <body><h2>${title}</h2><table><thead><tr>${headers.split(",").map((h) => `<th>${h}</th>`).join("")}</tr></thead>
    <tbody>${rows.map((r) => `<tr>${r.split(",").map((c) => `<td>${c}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`);
  win.document.close();
  win.print();
}

/* ─── Sort Icon ─── */
const SortIcon = ({ col, sortKey, sortDir }) => (
  <span className={`rpt-sort ${sortKey === col ? "rpt-sort-on" : "rpt-sort-off"}`}>
    {sortKey === col ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}
  </span>
);

/* ─── Skeleton Rows ─── */
const SkeletonRows = ({ cols, count }) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={i}>
      {cols.map((c) => (
        <td key={c.key} style={{ padding: "12px 16px" }}>
          <div className="rpt-skel" style={{ width: c.key === "action" ? 56 : "80%" }} />
        </td>
      ))}
    </tr>
  ));

/* ─── Pagination ─── */
function PaginationBar({ meta, page, setPage, rowsPerPage, setRowsPerPage, loading }) {
  const totalPages   = meta?.last_page ?? 1;
  const totalRecords = meta?.total     ?? 0;
  const from         = meta?.from      ?? 0;
  const to           = meta?.to        ?? 0;
  return (
    <div className="rpt-pagination">
      <div className="rpt-rpp">
        <span>Rows per page:</span>
        <select className="rpt-rpp-sel" value={rowsPerPage}
          onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
          {ROWS_OPTIONS.map((n) => <option key={n}>{n}</option>)}
        </select>
      </div>
      <span className="rpt-pg-info">
        {totalRecords === 0 ? "0 results" : `${from}–${to} of ${totalRecords}`}
      </span>
      <div className="rpt-pg-nav">
        <button className="rpt-pg-btn" onClick={() => setPage(1)}             disabled={page === 1 || loading}>«</button>
        <button className="rpt-pg-btn" onClick={() => setPage((p) => p - 1)} disabled={page === 1 || loading}>‹</button>
        <span className="rpt-pg-cur">{page} / {totalPages}</span>
        <button className="rpt-pg-btn" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages || loading}>›</button>
        <button className="rpt-pg-btn" onClick={() => setPage(totalPages)}   disabled={page === totalPages || loading}>»</button>
      </div>
    </div>
  );
}

/* ─── Error Banner ─── */
const ErrorBanner = ({ error, onRetry }) => !error ? null : (
  <div className="rpt-error-banner">
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    {error}
    <button className="rpt-retry" onClick={onRetry}>Retry</button>
  </div>
);

/* ─── Empty State ─── */
const EmptyState = ({ message, onClear }) => (
  <div className="rpt-empty-state">
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.2">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 9h6M9 13h4" strokeLinecap="round" />
    </svg>
    <span>{message}</span>
    {onClear && <button className="rpt-apply-btn" style={{ marginTop: 6 }} onClick={onClear}>Clear Filters</button>}
  </div>
);

/* ─── Shared Main Filter Bar ─── */
function MainFilterBar({ loading, dateRange, setDateRange, search, setSearch,
  statusFilter, setStatusFilter, staffFilter, setStaffFilter, onApply }) {
  return (
    <div className={`rpt-filter-bar${loading ? " rpt-filter-loading" : ""}`}>
      <div className="rpt-pills">
        {DATE_RANGES.map((r) => (
          <button key={r}
            className={`rpt-pill${dateRange === r ? " rpt-pill-on" : ""}`}
            onClick={() => setDateRange(r === dateRange ? null : r)}>
            {r}
          </button>
        ))}
      </div>
      <div className="rpt-filter-right">
        <div className="rpt-search-wrap">
          <svg width="13" height="13" viewBox="0 0 20 20" fill="none" className="rpt-search-ico">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
            <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <input className="rpt-search" type="text"
            placeholder="Search by job ID, client, staff…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onApply()} />
        </div>
        <select className="rpt-sel" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">Any Status</option>
          <option value="pending">Pending</option>
          <option value="ongoing">Ongoing</option>
          <option value="hold">Hold</option>
          <option value="completed">Completed</option>
          <option value="revisit">Revisit</option>
        </select>
        <select className="rpt-sel" value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)}>
          <option value="">All Staff</option>
        </select>
        <button className="rpt-apply-btn" onClick={onApply} disabled={loading}>
          {loading ? "Loading…" : "Apply Filters"}
        </button>
      </div>
    </div>
  );
}

/* ─── Custom date range bar ─── */
const CustomDateBar = ({ fromDate, setFromDate, toDate, setToDate, onApply, loading }) => (
  <div className="rpt-custom-date">
    <label className="rpt-dr-lbl">From</label>
    <input type="date" className="rpt-date-inp" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
    <label className="rpt-dr-lbl">To</label>
    <input type="date" className="rpt-date-inp" value={toDate}   onChange={(e) => setToDate(e.target.value)} />
    <button className="rpt-apply-btn" onClick={onApply} disabled={loading || !fromDate || !toDate}>Apply</button>
  </div>
);

/* ═══════════════════════════════════════════════════
   ROOT COMPONENT
═══════════════════════════════════════════════════ */
export default function JobReports() {
  const navigate = useNavigate();
  const [showSidebar,    setShowSidebar]    = useState(false);
  const [activeTab,      setActiveTab]      = useState("job");
  const [exportHandlers, setExportHandlers] = useState({ csv: null, pdf: null });

  return (
    <div className="app">
      <Sidebar show={showSidebar} closeSidebar={() => setShowSidebar(false)} />
      <div className="main">
        <Topbar toggleSidebar={() => setShowSidebar(true)} />
        <div className="rpt-page">

          <div className="rpt-header">
            <h2 className="rpt-title">Reports</h2>
            <div className="rpt-export-row">
              <button className="rpt-export-btn" onClick={() => exportHandlers.csv?.()}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2v8M5 7l3 3 3-3M2 11v1a2 2 0 002 2h8a2 2 0 002-2v-1"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Export CSV
              </button>
              <button className="rpt-export-btn" onClick={() => exportHandlers.pdf?.()}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M5 6h6M5 9h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                Export PDF
              </button>
            </div>
          </div>

          <div className="rpt-tabs">
            {[
              { key: "job",      label: "Job Report"           },
              { key: "activity", label: "Productivity Report"  },
              { key: "material", label: "Material Used Report" },
            ].map(({ key, label }) => (
              <button key={key}
                className={`rpt-tab${activeTab === key ? " rpt-tab-on" : ""}`}
                onClick={() => setActiveTab(key)}>
                {label}
              </button>
            ))}
          </div>

          {activeTab === "job"      && <JobReportTab      navigate={navigate} reg={setExportHandlers} />}
          {activeTab === "activity" && <ActivityReportTab navigate={navigate} reg={setExportHandlers} />}
          {activeTab === "material" && <MaterialReportTab navigate={navigate} reg={setExportHandlers} />}

        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   TAB 1 — JOB REPORT
═══════════════════════════════════════════════════ */
function JobReportTab({ navigate, reg }) {
  const [search,       setSearch]      = useState("");
  const [statusFilter, setStatusFilter]= useState("all");
  const [staffFilter,  setStaffFilter] = useState("");
  const [dateRange,    setDateRange]   = useState(null);
  const [fromDate,     setFromDate]    = useState("");
  const [toDate,       setToDate]      = useState("");
  const [page,         setPage]        = useState(1);
  const [rowsPerPage,  setRowsPerPage] = useState(10);
  const [jobs,         setJobs]        = useState([]);
  const [meta,         setMeta]        = useState(null);
  const [loading,      setLoading]     = useState(false);
  const [error,        setError]       = useState("");
  const [sortKey,      setSortKey]     = useState(null);
  const [sortDir,      setSortDir]     = useState("asc");

  const filtersRef = useRef({});
  filtersRef.current = { page, rowsPerPage, search, statusFilter, staffFilter, dateRange, fromDate, toDate };

  const buildParams = (ov = {}) => {
    const f = filtersRef.current;
    return {
      page:        f.page,
      per_page:    f.rowsPerPage,
      search:      f.search       || undefined,
      status:      f.statusFilter !== "all" ? f.statusFilter : undefined,
      staff_id:    f.staffFilter  || undefined,
      filter_type: f.dateRange    ? FILTER_TYPE_MAP[f.dateRange] : undefined,
      from_date:   f.dateRange === "Custom" ? f.fromDate : undefined,
      to_date:     f.dateRange === "Custom" ? f.toDate   : undefined,
      ...ov,
    };
  };

  /* Fetch — triggered only when pagination changes */
  useEffect(() => {
    setLoading(true);
    setError("");
    getJobs(buildParams())
      .then((res) => {
        if (res.data.success) { setJobs(res.data.data.data ?? []); setMeta(res.data.data); }
        else setError(res.data.message || "Failed.");
      })
      .catch((e) => setError(e?.response?.data?.message || "Error."))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage]); // eslint-disable-line

  /* Apply filters button / Enter key */
  const apply = () => {
    setLoading(true);
    setError("");
    getJobs(buildParams({ page: 1 }))
      .then((res) => {
        if (res.data.success) { setJobs(res.data.data.data ?? []); setMeta(res.data.data); setPage(1); }
        else setError(res.data.message || "Failed.");
      })
      .catch((e) => setError(e?.response?.data?.message || "Error."))
      .finally(() => setLoading(false));
  };

  /* Export — registered once, always reads latest filters via ref */
  useEffect(() => {
    const run = async (dl) => {
      const res = await getJobs({ ...buildParams(), per_page: 9999, page: 1 });
      const rows = res.data?.data?.data ?? [];
      const hdr = "Job ID,Client,Location,Work Type,Staff,Start,Last Updated,Status";
      const csv = rows.map((j) => [
        fmtJobId(j.id),
        j.client_name,
        j.location,
        j.work_type,
        (j.staff?.length ? j.staff.map(s => s.name).join(" | ") : ""),
        fmt(j.created_at),
        fmt(j.updated_at),
        j.status,
      ].join(","));
      dl(hdr, csv);
    };
    reg({
      csv: () => run((h, r) => downloadCSV("job-report.csv", h, r)).catch(() => alert("Export failed.")),
      pdf: () => run((h, r) => downloadPDF("Job Report", h, r)).catch(() => alert("Export failed.")),
    });
  }, []); // eslint-disable-line

  const sort  = (k) => { if (sortKey === k) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("asc"); } };
  const clear = () => {
    setSearch(""); setStatusFilter("all"); setStaffFilter(""); setDateRange(null); setFromDate(""); setToDate("");
    setLoading(true); setError("");
    getJobs({ page: 1, per_page: rowsPerPage })
      .then((res) => {
        if (res.data.success) { setJobs(res.data.data.data ?? []); setMeta(res.data.data); setPage(1); }
        else setError(res.data.message || "Failed.");
      })
      .catch((e) => setError(e?.response?.data?.message || "Error."))
      .finally(() => setLoading(false));
  };

  const sorted = sortKey
    ? [...jobs].sort((a, b) => {
        const av = String(a[sortKey] ?? "").toLowerCase(), bv = String(b[sortKey] ?? "").toLowerCase();
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      })
    : jobs;

  const cols = [
    { key: "id",          label: "JOB ID"      },
    { key: "client_name", label: "CLIENT"       },
    { key: "location",    label: "LOCATION"     },
    { key: "work_type",   label: "WORK TYPE"    },
    { key: "staff_name",  label: "STAFF"        },
    { key: "created_at",  label: "START"        },
    { key: "updated_at",  label: "LAST UPDATED" },
    { key: "activity_duration", label: "DURATION" },
    { key: "status",      label: "STATUS"       },
    { key: "action",      label: "ACTION"       },
  ];

  return (
    <>
      <ErrorBanner error={error} onRetry={apply} />
      <MainFilterBar loading={loading} dateRange={dateRange} setDateRange={setDateRange}
        search={search} setSearch={setSearch} statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        staffFilter={staffFilter} setStaffFilter={setStaffFilter} onApply={apply} />
      {dateRange === "Custom" && <CustomDateBar fromDate={fromDate} setFromDate={setFromDate} toDate={toDate} setToDate={setToDate} onApply={apply} loading={loading} />}

      {/* Desktop table */}
      <div className="rpt-card rpt-desktop">
        {loading && <div className="rpt-overlay"><div className="rpt-spinner" /></div>}
        <div className="rpt-scroll">
          <table className="rpt-tbl">
            <thead><tr>{cols.map(({ key, label }) => (
              <th key={key} onClick={() => key !== "action" && sort(key)} style={{ cursor: key !== "action" ? "pointer" : "default" }}>
                {label}{key !== "action" && <SortIcon col={key} sortKey={sortKey} sortDir={sortDir} />}
              </th>
            ))}</tr></thead>
            <tbody>
              {loading ? <SkeletonRows cols={cols} count={rowsPerPage} /> :
               sorted.length === 0 ? <tr><td colSpan={cols.length}><EmptyState message="No jobs match your filters." onClear={clear} /></td></tr> :
               sorted.map((j) => (
                <tr key={j.id}>
                  <td className="rpt-td-id">{fmtJobId(j.id)}</td>
                  <td className="rpt-td-bold">{j.client_name}</td>
                  <td className="rpt-td-muted">{j.location}</td>
                  <td>{j.work_type}</td>
                  <td className="rpt-td-muted">
                    {j.staff?.length ? j.staff.map(s => s.name).join(", ") : "—"}
                  </td>
                  <td className="rpt-td-muted">{fmt(j.created_at)}</td>
                  <td className="rpt-td-muted">{fmt(j.updated_at)}</td>
                  <td className="rpt-td-muted">{j.activity_duration || "00:00:00"}</td>
                  <td><span className={`rpt-badge ${STATUS_COLORS[j.status] ?? "badge-gray"}`}>{capitalize(j.status)}</span></td>
                  <td><button className="rpt-view-btn" onClick={() => navigate(`/jobs/${j.id}`)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar meta={meta} page={page} setPage={setPage} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} loading={loading} />
      </div>

      {/* Mobile cards */}
      <div className="rpt-mobile">
        {loading ? <div className="rpt-mob-load"><div className="rpt-spinner" /></div> :
         sorted.length === 0 ? <div className="rpt-mob-empty">No jobs match your filters.</div> :
         sorted.map((j) => (
          <div key={j.id} className="rpt-mc">
            <div className="rpt-mc-top">
              <div><span className="rpt-mc-num">{fmtJobId(j.id)}</span><p className="rpt-mc-name">{j.client_name}</p></div>
              <span className={`rpt-badge ${STATUS_COLORS[j.status] ?? "badge-gray"}`}>{capitalize(j.status)}</span>
            </div>
            <div className="rpt-mc-body">
              {[
                ["Location",     j.location],
                ["Work Type",    j.work_type],
                ["Staff",        j.staff?.length ? j.staff.map(s => s.name).join(", ") : "—"],
                ["Start",        fmt(j.created_at)],
                ["Last Updated", fmt(j.updated_at)],
                ["Duration",     j.activity_duration || "00:00:00"],
              ].map(([k, v]) => (
                <div key={k} className="rpt-mc-row"><span className="rpt-mc-key">{k}</span><span>{v}</span></div>
              ))}
            </div>
            <div className="rpt-mc-foot">
              <button className="rpt-view-btn rpt-view-full" onClick={() => navigate(`/jobs/${j.id}`)}>View Details</button>
            </div>
          </div>
        ))}
        {(meta?.total ?? 0) > 0 && <PaginationBar meta={meta} page={page} setPage={setPage} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} loading={loading} />}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════
   TAB 2 — PRODUCTIVITY / ACTIVITY REPORT
═══════════════════════════════════════════════════ */
function ActivityReportTab({ navigate, reg }) {
  const [search,       setSearch]      = useState("");
  const [statusFilter, setStatusFilter]= useState("all");
  const [staffFilter,  setStaffFilter] = useState("");
  const [actType,      setActType]     = useState("all");
  const [dateRange,    setDateRange]   = useState(null);
  const [fromDate,     setFromDate]    = useState("");
  const [toDate,       setToDate]      = useState("");
  const [page,         setPage]        = useState(1);
  const [rowsPerPage,  setRowsPerPage] = useState(10);
  const [rows,         setRows]        = useState([]);
  const [meta,         setMeta]        = useState(null);
  const [loading,      setLoading]     = useState(false);
  const [error,        setError]       = useState("");
  const [sortKey,      setSortKey]     = useState(null);
  const [sortDir,      setSortDir]     = useState("asc");
  const [summary,      setSummary]     = useState({ active: 0, hold: 0, total: 0 });

  const filtersRef = useRef({});
  filtersRef.current = { page, rowsPerPage, search, statusFilter, staffFilter, actType, dateRange, fromDate, toDate };

  const buildParams = (ov = {}) => {
    const f = filtersRef.current;
    return {
      page:          f.page,
      per_page:      f.rowsPerPage,
      search:        f.search       || undefined,
      status:        f.statusFilter !== "all" ? f.statusFilter : undefined,
      staff_id:      f.staffFilter  || undefined,
      activity_type: f.actType      !== "all" ? f.actType : undefined,
      filter_type:   f.dateRange    ? FILTER_TYPE_MAP[f.dateRange] : undefined,
      from_date:     f.dateRange === "Custom" ? f.fromDate : undefined,
      to_date:       f.dateRange === "Custom" ? f.toDate   : undefined,
      ...ov,
    };
  };

  /* Fetch — pagination only */
  useEffect(() => {
    setLoading(true);
    setError("");
    apiGet("/jobs-Actvity", buildParams())
      .then((res) => {
        if (res.data.success) {
          const data = res.data.data.data ?? [];
          setRows(data); setMeta(res.data.data);
          setSummary({
            active: data.filter((a) => a.activity_type !== "Hold").reduce((s, a) => s + (a.duration_minutes ?? 0), 0),
            hold:   data.filter((a) => a.activity_type === "Hold").reduce((s, a) => s + (a.duration_minutes ?? 0), 0),
            total:  res.data.data.total ?? data.length,
          });
        } else setError(res.data.message || "Failed.");
      })
      .catch((e) => setError(e?.response?.data?.message || "Error."))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage]); // eslint-disable-line

  /* Apply filters */
  const apply = () => {
    setLoading(true);
    setError("");
    apiGet("/jobs-Actvity", buildParams({ page: 1 }))
      .then((res) => {
        if (res.data.success) {
          const data = res.data.data.data ?? [];
          setRows(data); setMeta(res.data.data); setPage(1);
          setSummary({
            active: data.filter((a) => a.activity_type !== "Hold").reduce((s, a) => s + (a.duration_minutes ?? 0), 0),
            hold:   data.filter((a) => a.activity_type === "Hold").reduce((s, a) => s + (a.duration_minutes ?? 0), 0),
            total:  res.data.data.total ?? data.length,
          });
        } else setError(res.data.message || "Failed.");
      })
      .catch((e) => setError(e?.response?.data?.message || "Error."))
      .finally(() => setLoading(false));
  };

  /* Export — registered once, ref keeps filters fresh */
  useEffect(() => {
    const run = async (dl) => {
      const res = await apiGet("/jobs-Actvity", { ...buildParams(), per_page: 9999, page: 1 });
      const d = res.data?.data?.data ?? [];
      const hdr = "Date,Staff,Job ID,Client,Activity,Start,End,Duration";
      const csv = d.map((a) => [
        fmt(a.created_at),
        a.staff_name,
        fmtJobId(a.job_id),
        a.client_name,
        a.activity_type,
        fmtTime(a.start_time),
        fmtTime(a.end_time),
        fmtDuration(a.duration_minutes),
      ].join(","));
      dl(hdr, csv);
    };
    reg({
      csv: () => run((h, r) => downloadCSV("activity-report.csv", h, r)).catch(() => alert("Export failed.")),
      pdf: () => run((h, r) => downloadPDF("Productivity Report", h, r)).catch(() => alert("Export failed.")),
    });
  }, []); // eslint-disable-line

  const sort = (k) => { if (sortKey === k) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("asc"); } };

  const sorted = sortKey
    ? [...rows].sort((a, b) => {
        const av = String(a[sortKey] ?? "").toLowerCase(), bv = String(b[sortKey] ?? "").toLowerCase();
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      })
    : rows;

  const cols = [
    { key: "created_at",       label: "DATE"     },
    { key: "staff_name",       label: "STAFF"    },
    { key: "job_id",           label: "JOB ID"   },
    { key: "client_name",      label: "CLIENT"   },
    { key: "activity_type",    label: "ACTIVITY" },
    { key: "start_time",       label: "START"    },
    { key: "end_time",         label: "END"      },
    { key: "duration_minutes", label: "DURATION" },
    { key: "action",           label: "ACTION"   },
  ];

  return (
    <>
      <ErrorBanner error={error} onRetry={apply} />
      <MainFilterBar loading={loading} dateRange={dateRange} setDateRange={setDateRange}
        search={search} setSearch={setSearch} statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        staffFilter={staffFilter} setStaffFilter={setStaffFilter} onApply={apply} />

      <div className="rpt-sub-row">
        <select className="rpt-sel rpt-sel-sub" value={actType} onChange={(e) => setActType(e.target.value)}>
          <option value="all">All Activity Types</option>
          <option value="Material Collection">Material Collection</option>
          <option value="Travelling">Travelling</option>
          <option value="Wrap Preparation">Wrap Preparation</option>
          <option value="Installation">Installation</option>
          <option value="Hold">Hold</option>
        </select>
      </div>

      {dateRange === "Custom" && <CustomDateBar fromDate={fromDate} setFromDate={setFromDate} toDate={toDate} setToDate={setToDate} onApply={apply} loading={loading} />}

      {/* Summary cards */}
      <div className="rpt-summary">
        <div className="rpt-sum-card rpt-sum-blue">
          <div className="rpt-sum-val">{fmtHM(summary.active)}</div>
          <div className="rpt-sum-lbl">Total Active Hours</div>
        </div>
        <div className="rpt-sum-card rpt-sum-orange">
          <div className="rpt-sum-val">{fmtHM(summary.hold)}</div>
          <div className="rpt-sum-lbl">Total Hold Hours</div>
        </div>
        <div className="rpt-sum-card rpt-sum-indigo">
          <div className="rpt-sum-val">{summary.total}</div>
          <div className="rpt-sum-lbl">Activities Logged</div>
        </div>
      </div>

      {/* Desktop table */}
      <div className="rpt-card rpt-desktop">
        {loading && <div className="rpt-overlay"><div className="rpt-spinner" /></div>}
        <div className="rpt-scroll">
          <table className="rpt-tbl">
            <thead><tr>{cols.map(({ key, label }) => (
              <th key={key} onClick={() => key !== "action" && sort(key)} style={{ cursor: key !== "action" ? "pointer" : "default" }}>
                {label}{key !== "action" && <SortIcon col={key} sortKey={sortKey} sortDir={sortDir} />}
              </th>
            ))}</tr></thead>
            <tbody>
              {loading ? <SkeletonRows cols={cols} count={rowsPerPage} /> :
               sorted.length === 0 ? <tr><td colSpan={cols.length}><EmptyState message="No activities match your filters." /></td></tr> :
               sorted.map((a) => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600, color: "#111827" }}>{fmt(a.created_at)}</td>
                  <td>{a.staff_name ?? "—"}</td>
                  <td style={{ fontWeight: 600, color: "#2563eb" }}>{fmtJobId(a.job_id)}</td>
                  <td className="rpt-td-bold">{a.client_name}</td>
                  <td><span className={`rpt-badge ${ACTIVITY_COLORS[a.activity_type] ?? "badge-gray"}`}>{a.activity_type}</span></td>
                  <td className="rpt-td-muted">{fmtTime(a.start_time)}</td>
                  <td className="rpt-td-muted">{fmtTime(a.end_time)}</td>
                  <td>{fmtDuration(a.duration_minutes)}</td>
                  <td><button className="rpt-view-btn" onClick={() => navigate(`/jobs/${a.job_id}`)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar meta={meta} page={page} setPage={setPage} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} loading={loading} />
      </div>

      {/* Mobile cards */}
      <div className="rpt-mobile">
        {loading ? <div className="rpt-mob-load"><div className="rpt-spinner" /></div> :
         sorted.length === 0 ? <div className="rpt-mob-empty">No activities match your filters.</div> :
         sorted.map((a) => (
          <div key={a.id} className="rpt-mc">
            <div className="rpt-mc-top">
              <div><span className="rpt-mc-num">{fmtJobId(a.job_id)}</span><p className="rpt-mc-name">{a.client_name}</p></div>
              <span className={`rpt-badge ${ACTIVITY_COLORS[a.activity_type] ?? "badge-gray"}`}>{a.activity_type}</span>
            </div>
            <div className="rpt-mc-body">
              {[
                ["Date",     fmt(a.created_at)],
                ["Staff",    a.staff_name ?? "—"],
                ["Start",    fmtTime(a.start_time)],
                ["End",      fmtTime(a.end_time)],
                ["Duration", fmtDuration(a.duration_minutes)],
              ].map(([k, v]) => (
                <div key={k} className="rpt-mc-row"><span className="rpt-mc-key">{k}</span><span>{v}</span></div>
              ))}
            </div>
            <div className="rpt-mc-foot">
              <button className="rpt-view-btn rpt-view-full" onClick={() => navigate(`/jobs/${a.job_id}`)}>View Details</button>
            </div>
          </div>
        ))}
        {(meta?.total ?? 0) > 0 && <PaginationBar meta={meta} page={page} setPage={setPage} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} loading={loading} />}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════
   TAB 3 — MATERIAL USED REPORT
═══════════════════════════════════════════════════ */
function MaterialReportTab({ navigate, reg }) {
  const [search,       setSearch]      = useState("");
  const [statusFilter, setStatusFilter]= useState("all");
  const [staffFilter,  setStaffFilter] = useState("");
  const [matName,      setMatName]     = useState("all");
  const [dateRange,    setDateRange]   = useState(null);
  const [fromDate,     setFromDate]    = useState("");
  const [toDate,       setToDate]      = useState("");
  const [page,         setPage]        = useState(1);
  const [rowsPerPage,  setRowsPerPage] = useState(10);
  const [rows,         setRows]        = useState([]);
  const [meta,         setMeta]        = useState(null);
  const [loading,      setLoading]     = useState(false);
  const [error,        setError]       = useState("");
  const [sortKey,      setSortKey]     = useState(null);
  const [sortDir,      setSortDir]     = useState("asc");

  const filtersRef = useRef({});
  filtersRef.current = { page, rowsPerPage, search, statusFilter, staffFilter, matName, dateRange, fromDate, toDate };

  const buildParams = (ov = {}) => {
    const f = filtersRef.current;
    return {
      page:          f.page,
      per_page:      f.rowsPerPage,
      search:        f.search       || undefined,
      status:        f.statusFilter !== "all" ? f.statusFilter : undefined,
      staff_id:      f.staffFilter  || undefined,
      material_name: f.matName      !== "all" ? f.matName : undefined,
      filter_type:   f.dateRange    ? FILTER_TYPE_MAP[f.dateRange] : undefined,
      from_date:     f.dateRange === "Custom" ? f.fromDate : undefined,
      to_date:       f.dateRange === "Custom" ? f.toDate   : undefined,
      ...ov,
    };
  };

  /* Fetch — pagination only */
  useEffect(() => {
    setLoading(true);
    setError("");
    apiGet("/jobs-material", buildParams())
      .then((res) => {
        if (res.data.success) { setRows(res.data.data.data ?? []); setMeta(res.data.data); }
        else setError(res.data.message || "Failed.");
      })
      .catch((e) => setError(e?.response?.data?.message || "Error."))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage]); // eslint-disable-line

  /* Apply filters */
  const apply = () => {
    setLoading(true);
    setError("");
    apiGet("/jobs-material", buildParams({ page: 1 }))
      .then((res) => {
        if (res.data.success) { setRows(res.data.data.data ?? []); setMeta(res.data.data); setPage(1); }
        else setError(res.data.message || "Failed.");
      })
      .catch((e) => setError(e?.response?.data?.message || "Error."))
      .finally(() => setLoading(false));
  };

  /* Export — registered once, ref keeps filters fresh */
  useEffect(() => {
    const run = async (dl) => {
      const res = await apiGet("/jobs-material", { ...buildParams(), per_page: 9999, page: 1 });
      const d = res.data?.data?.data ?? [];
      const hdr = "Date,Job ID,Client,Staff,Material,Quantity";
      const csv = d.map((m) => [
        fmt(m.created_at),
        fmtJobId(m.job_id),
        m.client_name,
        m.staff_name,
        m.material_name,
        m.quantity,
      ].join(","));
      dl(hdr, csv);
    };
    reg({
      csv: () => run((h, r) => downloadCSV("material-report.csv", h, r)).catch(() => alert("Export failed.")),
      pdf: () => run((h, r) => downloadPDF("Material Used Report", h, r)).catch(() => alert("Export failed.")),
    });
  }, []); // eslint-disable-line

  const sort = (k) => { if (sortKey === k) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("asc"); } };

  const sorted = sortKey
    ? [...rows].sort((a, b) => {
        const av = String(a[sortKey] ?? "").toLowerCase(), bv = String(b[sortKey] ?? "").toLowerCase();
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      })
    : rows;

  const cols = [
    { key: "created_at",    label: "DATE"     },
    { key: "job_id",        label: "JOB ID"   },
    { key: "client_name",   label: "CLIENT"   },
    { key: "staff_name",    label: "STAFF"    },
    { key: "material_name", label: "MATERIAL" },
    { key: "quantity",      label: "QUANTITY" },
    { key: "action",        label: "ACTION"   },
  ];

  return (
    <>
      <ErrorBanner error={error} onRetry={apply} />
      <MainFilterBar loading={loading} dateRange={dateRange} setDateRange={setDateRange}
        search={search} setSearch={setSearch} statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        staffFilter={staffFilter} setStaffFilter={setStaffFilter} onApply={apply} />

      <div className="rpt-sub-row">
        <select className="rpt-sel rpt-sel-sub" value={matName} onChange={(e) => setMatName(e.target.value)}>
          <option value="all">All Materials</option>
          <option value="Paint">Paint</option>
          <option value="Paint Brushes">Paint Brushes</option>
          <option value="Drywall Sheets">Drywall Sheets</option>
          <option value="Joint Compound">Joint Compound</option>
          <option value="Vinyl Wrap Rolls">Vinyl Wrap Rolls</option>
        </select>
      </div>

      {dateRange === "Custom" && <CustomDateBar fromDate={fromDate} setFromDate={setFromDate} toDate={toDate} setToDate={setToDate} onApply={apply} loading={loading} />}

      {/* Desktop table */}
      <div className="rpt-card rpt-desktop">
        {loading && <div className="rpt-overlay"><div className="rpt-spinner" /></div>}
        <div className="rpt-scroll">
          <table className="rpt-tbl">
            <thead><tr>{cols.map(({ key, label }) => (
              <th key={key} onClick={() => key !== "action" && sort(key)} style={{ cursor: key !== "action" ? "pointer" : "default" }}>
                {label}{key !== "action" && <SortIcon col={key} sortKey={sortKey} sortDir={sortDir} />}
              </th>
            ))}</tr></thead>
            <tbody>
              {loading ? <SkeletonRows cols={cols} count={rowsPerPage} /> :
               sorted.length === 0 ? <tr><td colSpan={cols.length}><EmptyState message="No materials match your filters." /></td></tr> :
               sorted.map((m) => (
                <tr key={m.id}>
                  <td style={{ fontWeight: 600, color: "#111827" }}>{fmt(m.created_at)}</td>
                  <td style={{ fontWeight: 600, color: "#2563eb" }}>{fmtJobId(m.job_id)}</td>
                  <td className="rpt-td-bold">{m.client_name}</td>
                  <td className="rpt-td-muted">{m.staff_name ?? "—"}</td>
                  <td><span className={`rpt-badge ${MATERIAL_COLORS[m.material_name] ?? "badge-gray"}`}>{m.material_name}</span></td>
                  <td>{m.quantity}</td>
                  <td><button className="rpt-view-btn" onClick={() => navigate(`/jobs/${m.job_id}`)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar meta={meta} page={page} setPage={setPage} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} loading={loading} />
      </div>

      {/* Mobile cards */}
      <div className="rpt-mobile">
        {loading ? <div className="rpt-mob-load"><div className="rpt-spinner" /></div> :
         sorted.length === 0 ? <div className="rpt-mob-empty">No materials match your filters.</div> :
         sorted.map((m) => (
          <div key={m.id} className="rpt-mc">
            <div className="rpt-mc-top">
              <div><span className="rpt-mc-num">{fmtJobId(m.job_id)}</span><p className="rpt-mc-name">{m.client_name}</p></div>
              <span className={`rpt-badge ${MATERIAL_COLORS[m.material_name] ?? "badge-gray"}`}>{m.material_name}</span>
            </div>
            <div className="rpt-mc-body">
              {[
                ["Date",     fmt(m.created_at)],
                ["Staff",    m.staff_name ?? "—"],
                ["Quantity", m.quantity],
              ].map(([k, v]) => (
                <div key={k} className="rpt-mc-row"><span className="rpt-mc-key">{k}</span><span>{v}</span></div>
              ))}
            </div>
            <div className="rpt-mc-foot">
              <button className="rpt-view-btn rpt-view-full" onClick={() => navigate(`/jobs/${m.job_id}`)}>View Details</button>
            </div>
          </div>
        ))}
        {(meta?.total ?? 0) > 0 && <PaginationBar meta={meta} page={page} setPage={setPage} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} loading={loading} />}
      </div>
    </>
  );
}